import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * NativeSelect — a `<select>` with the field shape (ADR-0088 D2).
 *
 * The customer surfaces keep the browser's own picker: on a phone it is the
 * wheel or sheet people already know, which no styled popup matches (the
 * familiar-by-default rule). It looks like a `Select` trigger of the same
 * size and surface, so the two can sit in one form or filter rail.
 *
 * `className` sizes and places the field: it goes on the wrapper that also
 * holds the chevron, and the `<select>` fills it. Every other prop goes to
 * the `<select>`.
 *
 * `icon` leads the field with a decorative icon, placed like the chevron
 * mirrored, for a filter whose options name themselves ("All accents",
 * "British accent") and so needs no visible label. The select still needs
 * its own accessible name.
 */
import { Icon } from './icon.js';
import { forwardRef } from 'react';
import { cn } from './cn.js';
import { selectTriggerVariants } from './select.js';
/** Room for the chevron, and where it sits, per size. */
const chevron = {
    sm: { select: 'pr-8', icon: 'right-2' },
    default: { select: 'pr-9', icon: 'right-3' },
    lg: { select: 'pr-11', icon: 'right-4' },
};
/** Room for a leading icon, and where it sits: the chevron's, mirrored. */
const leading = {
    sm: { select: 'pl-7', icon: 'left-2' },
    default: { select: 'pl-9', icon: 'left-3' },
    lg: { select: 'pl-11', icon: 'left-4' },
};
export const NativeSelect = forwardRef(({ className, surface, size = 'default', icon, children, ...props }, ref) => {
    const room = chevron[size ?? 'default'];
    const lead = leading[size ?? 'default'];
    return (_jsxs("div", { "data-slot": "native-select", "data-size": size, className: cn('relative w-full', className), children: [_jsx("select", { ref: ref, className: cn(selectTriggerVariants({ surface, size }), 'appearance-none', room.select, icon && lead.select), ...props, children: children }), icon && (_jsx(Icon, { name: icon, "data-slot": "native-select-icon", className: cn('pointer-events-none absolute top-1/2 -translate-y-1/2 text-text-secondary', lead.icon) })), _jsx(Icon, { name: "expand", className: cn('pointer-events-none absolute top-1/2 -translate-y-1/2 text-text-primary opacity-60', room.icon) })] }));
});
NativeSelect.displayName = 'NativeSelect';
//# sourceMappingURL=native-select.js.map